import React, { useState, useMemo, useEffect } from 'react'
import ExpenseService from '@/modules/expense/ExpenseService'
import RevenueService from '@/modules/revenue/revenueService'
import { makeStyles } from '@mui/styles'
import { user } from 'ampersand-app'
import { USER_RIGHTS, SUBSCRIPTION_MODULES, experimentalFeatures } from '@/modules/base/util/constants'
import ContextMenuItem from '@/modules/base/components/context-menu/context-menu-item/context-menu-item'
import { SquareIconButton } from '@/modules/base/components/buttons/index'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconMenu from '@assets/images/icons/menu.svg'
import BankIcon from '@assets/images/icons/bank.svg'
import IconDelete from '@assets/images/icons/trashcan.svg'

import lang from '@/lang'
import IconAdd from '@assets/images/icons/add.svg'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'
import { PropTypes } from 'prop-types'

import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'

const CASH_JOURNAL_ACTIONS = {
  LINK_INVOICES: 'linkInvoices',
  CREATE_EXPENSE: 'createExpense',
  CREATE_REVENUE: 'createRevenue',
  PRIVATE_BOOKING_DEPOSIT: 'privateBookingDeposit',
  PRIVATE_BOOKING_WITHDRAWAL: 'privateBookingWithdrawal',
}

const DocumentDetailActionButtons = (props) => {
  const {
    file,
    expense,
    revenue,
    onNavigate,
    variant,
    onRemove,
  } = props
  const [expenseData, setExpenseData] = useState()
  const [revenueData, setRevenueData] = useState()
  const hasRevenueUserRights = useCheckAccessRights(USER_RIGHTS.REVENUES.WRITE)
  const hasExpenseUserRights = useCheckAccessRights(USER_RIGHTS.EXPENSES.WRITE)
  const hasRevenueRights = useCheckModuleAccess(SUBSCRIPTION_MODULES.REVENUES) && hasRevenueUserRights
  const hasExpenseRights = useCheckModuleAccess(SUBSCRIPTION_MODULES.EXPENSES) && hasExpenseUserRights
  const hasPaymentsRights = useCheckModuleAccess(SUBSCRIPTION_MODULES.BANKING)
  const classes = useStyles(props)
  const isIncomingInvoice = file?.tags?.find(({ isSystem, tagType }) => !!isSystem && tagType === 'incomingInvoice')

  const expenseId = useMemo(() => {
    return expense && expense[0]?.id
  }, [expense])

  const revenueId = useMemo(() => {
    return revenue && revenue[0]?.id
  }, [revenue])

  useEffect(() => {
    if (expenseId && hasExpenseUserRights) {
      ExpenseService.getById(expenseId)
        .then(({ data: responseData }) => {
          setExpenseData(responseData)
        })
    }
    if (revenueId && hasRevenueRights) {
      RevenueService.getById(revenueId)
        .then(({ data: responseData }) => {
          setRevenueData(responseData)
        })
    }
  }, [expenseId, revenueId])

  const onCashJournalButtonClick = (action) => {
    switch (action) {
      case CASH_JOURNAL_ACTIONS.CREATE_EXPENSE:
        // FLAG:useDocuAi — on → central review page; off → legacy expense-from-file form.
        onNavigate(user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
          ? `/documents/review/${file.id}`
          : `/expense/new/file/${file.id}`)
        break
      case CASH_JOURNAL_ACTIONS.CREATE_REVENUE:
        onNavigate(`/revenue/new/file/${file.id}`)
        break
    }
  }

  const handleAddClick = () => {
    onNavigate(`/expense/edit/${expenseId}/pay`)
  }

  const revenueContextMenuItem = hasRevenueRights && !file?.isAssigned && isIncomingInvoice
    ? (<ContextMenuItem
      label={lang.banking.list.createRevenue}
      icon={<SVGIcon svg={IconAdd} width={'14px'} height={'14px'} />}
      onItemClick={() => onCashJournalButtonClick(CASH_JOURNAL_ACTIONS.CREATE_REVENUE)}
    />)
    : null
  const expenseContextMenuItem = hasExpenseRights && !file?.isAssigned && isIncomingInvoice
    ? (<ContextMenuItem
      label={lang.banking.list.createOutput}
      icon={<SVGIcon svg={IconAdd} width={'14px'} height={'14px'} />}
      onItemClick={() => onCashJournalButtonClick(CASH_JOURNAL_ACTIONS.CREATE_EXPENSE)}
    />)
    : null
  const PaymentContextMenuItem = hasPaymentsRights && file?.isAssigned && expenseData?.state === 'open'
    ? (<ContextMenuItem
      label={lang.banking.list.payment}
      icon={<SVGIcon svg={BankIcon} width={'14px'} height={'14px'} />}
      onItemClick={handleAddClick}
    />)
    : null
  const deleteEnabled = !file?.isAssigned && !file?.externalOwnerSystem
  const DeleteMenuItem = deleteEnabled
    ? (<ContextMenuItem
      label={lang.generics.delete}
      icon={<SVGIcon svg={IconDelete} width={'14px'} height={'14px'} />}
      onItemClick={onRemove}
    />)
    : null

  const checkButtonDisabled = (
    expenseData?.state === 'paid' || revenueData || file?.type === 'invoice'
  )

  const iconColor = variant === 'primary'
    ? '#fff'
    : '#63849f'

  if (!(hasPaymentsRights || hasExpenseRights || hasRevenueRights)) {
    return (
      <SquareIconButton
        onClick={onRemove}
        icon={IconDelete}
        disabled={!deleteEnabled}
        size='large'
        iconColor={iconColor}
        variant={variant} />
    )
  }

  return (
    <div>
      <SquareIconButton
        icon={IconMenu}
        disabled={checkButtonDisabled}
        size='large'
        iconColor={iconColor}
        variant={variant}
        popperStyle={{ popperContent: classes.popperContent }}
      >
        {expenseContextMenuItem}
        {revenueContextMenuItem}
        {PaymentContextMenuItem}
        {DeleteMenuItem}
      </SquareIconButton>
    </div>
  )
}

const useStyles = makeStyles(() => ({
  popperContent: {
    padding: '16px 0',
  },
}))

DocumentDetailActionButtons.propTypes = {
  file: PropTypes.object.isRequired,
  expense: PropTypes.array,
  revenue: PropTypes.array,
  variant: PropTypes.string,
  onNavigate: PropTypes.func,
  onScan: PropTypes.func,
  onRemove: PropTypes.func,
  onSign: PropTypes.func,
}

DocumentDetailActionButtons.defaultProps = {
  variant: 'primary',
}

export default DocumentDetailActionButtons
